// ---------------------------------------------------------------------------
// Announcements API client — Round 9 (2026).
// Wraps /api/announcements so React components don't have to know the URLs
// or remember to call .data on every response.
// ---------------------------------------------------------------------------
import api from "../api";

/** List all active announcements (newest first). */
export const fetchAnnouncements = async () => {
    const res = await api.get("/announcements");
    return Array.isArray(res.data) ? res.data : [];
};

/** Quick probe — does the current user have HR/Admin/SuperUser permission to
 *  create or edit announcements? Used to gate the "+ New Announcement"
 *  button in the dashboard. Returns false on any error so we fail closed. */
export const canManageAnnouncements = async () => {
    try {
        const res = await api.get("/announcements/can-manage");
        return !!res?.data?.canManage;
    } catch { return false; }
};

/**
 * Create a new announcement.
 * @param {Object} payload
 * @param {string} payload.title
 * @param {string} payload.body
 * @param {string} [payload.imageBase64]   Raw base64 OR a "data:image/png;base64,..." URL.
 * @param {string} [payload.imageMimeType] Optional — inferred from data URL if omitted.
 */
export const createAnnouncement = async (payload) => {
    const res = await api.post("/announcements", payload);
    return res.data;
};

/**
 * Update an existing announcement.
 * Pass clearImage=true to remove the image without uploading a new one.
 */
export const updateAnnouncement = async (id, payload) => {
    const res = await api.put(`/announcements/${id}`, payload);
    return res.data;
};

/** Soft-delete (server flips IsActive=false). */
export const deleteAnnouncement = async (id) => {
    await api.delete(`/announcements/${id}`);
};

/**
 * Convert a File from an &lt;input type="file" /&gt; to the
 * {imageBase64, imageMimeType} shape the API expects. Resolves to null
 * for an empty/invalid file.
 */
export const fileToImagePayload = (file) =>
    new Promise((resolve, reject) => {
        if (!file) return resolve(null);
        const reader = new FileReader();
        reader.onload = () => {
            // reader.result is "data:image/png;base64,iVBORw0KGgo..."
            const dataUrl = String(reader.result || "");
            const m = dataUrl.match(/^data:([^;]+);base64,(.*)$/);
            if (!m) return resolve(null);
            resolve({ imageMimeType: m[1], imageBase64: m[2] });
        };
        reader.onerror = () => reject(new Error("Could not read file."));
        reader.readAsDataURL(file);
    });

/** Build the `src` attribute for an &lt;img&gt; from an announcement DTO. */
export const announcementImageSrc = (a) =>
    a?.imageBase64 ? `data:${a.imageMimeType || "image/png"};base64,${a.imageBase64}` : null;
